import React, { Component } from "react";
import { Link, RouteComponentProps } from "react-router-dom";
import styled from "styled-components";
import { transparentize } from "polished";
import { useUserSettings } from "components/Site/Contexts/UserSettings";

import { FaSearchMinus as NoResultsIcon } from "react-icons/fa";
import { MdMoreHoriz as LoadMoreIcon } from "react-icons/md";
import SearchIcon from "icons/SearchIcon";
import CloseIcon from "icons/CloseIcon";
import CloudOffIcon from "icons/CloudOffIcon";

import { search } from "../../api/BibleApi";
import { parseQueryString } from "../../utils/paths";
import AppBar from "../AppBar/AppBar";
import {
  PickerContainer,
  PickerGroup,
  StyledButtonItem,
  SearchButtonItem,
  AppBarSpacer
} from "../UI/Pickers";
import media from "../../utils/media";
import Viewer from "../UI/Viewer";

import SearchInput from "./SearchInput";
import Passage from "./Passage";
import Paginator from "./Paginator";
import { usePageTracker } from "utils/analytics/hooks";
import { isMobileDevice } from "../../utils/mobile";
import { chapterPath } from "../../utils/paths";
import {
  SearchResultData,
  SearchResultVerse,
  SearchResultPassage
} from "../../api/typedefs/SearchResults";
import { useNetworkStatus } from "utils/hooks";
import Loader from "components/UI/Loader";

const BottomGradient = styled.div`
  display: block;
  position: absolute;
  top: 8em;
  width: 100%;
  height: 4em;
  transition: background 0.6s ease;
  background: linear-gradient(transparent, rgba(255, 255, 255, 1));
`;

const SearchResult = styled.div<{ verse?: boolean; key?: string }>`
  position: relative;
  /* font-family: ${props => props.theme.bibleFont}; */
  font-family: 'Noto Serif', serif;
  margin: 0;

    // hide section header and refs in search resault
   .s, .s1, .s2, .s3, .s4, .s5, .r{
     display: none;
   }
  .scripture-styles, p {
    overflow: ${props => (props.verse ? "auto" : "hidden")};
    max-height: ${props => (props.verse ? "none" : "8em")};
  }

  ${media.desktop`margin:0 64px;`};
  direction: ${props => props.theme.scriptDirection || "ltr"};
  border-radius: 2px;
  h3, p {
    font-size:16px;
    margin:0;
    padding:0;
  };
  h3 {
    margin-bottom:.3em;
    font-weight: 700;
  };
  a {
    color: ${props => props.theme.primaryText};
    display: block;
    padding: 16px;
    text-decoration: none;
    transition: background-color 0.6s ease;
    &:before {
      transition: background 0.6s ease;
      background: linear-gradient(transparent, rgba(255,0,255, 1));
    }
    &:hover {
      background-color: ${({ theme }) => `
      ${transparentize(0.9, theme.hightlightColor)}
    `};

      &:after {
        background: linear-gradient(transparent 10px, rgba(255,255,255, 0));
      }
    }
  };
`;

const LoadMore = styled.div`
  width: 100%;
  margin-bottom: 1.5rem;
  text-align: center;
  svg {
    fill: ${props => props.theme.computedPrimary};
    border: solid 2px ${props => props.theme.computedPrimary};
    border-radius: 100px;
  }
`;

const StyledEmptyContent = styled.div`
  display: flex;
  justify-content: center;
`;

interface SearchResultsProps extends RouteComponentProps<{ bibleId: string }> {
  isOnline: boolean;
  darkMode: boolean;
}

interface SearchResultsState {
  results: SearchResultData;
  activePage: number;
  resultsPerPage: number;
  isLoading: boolean;
  canClose: boolean;
  embed: boolean;
  fumsToken: string;
}

const defaultResultsState: SearchResultData = {
  query: "",
  limit: 0,
  offset: 0,
  total: 0,
  verses: [] as SearchResultVerse[],
  passages: [] as SearchResultPassage[]
};

class SearchResults extends Component<SearchResultsProps, SearchResultsState> {
  state = {
    results: { ...defaultResultsState },
    fumsToken: "",
    activePage: 1,
    resultsPerPage: 10,
    isLoading: false,
    canClose: false,
    embed: false
  };

  componentDidMount() {
    this.fetchSearchResults({ initialLoad: true });
  }

  componentDidUpdate(
    prevProps: SearchResultsProps,
    prevState: SearchResultsState
  ) {
    if (
      prevState.activePage !== this.state.activePage ||
      prevProps.location.search !== this.props.location.search ||
      (prevProps.isOnline === false && this.props.isOnline === true) // if we come back online
    ) {
      this.fetchSearchResults();
      window.scrollTo(0, 0);
    }

    const newFumsId = this.state.fumsToken;
    if (newFumsId && prevState.fumsToken !== newFumsId) {
      (window as any).fums('trackView', newFumsId);
    }
  }

  fetchSearchResults({ initialLoad = false } = {}) {
    const { q } = parseQueryString(this.props.location.search);
    const query = "" + q;

    if (!this.props.isOnline) {
      return this.setState({
        results: {
          query,
          ...defaultResultsState
        }
      });
    }

    const { bibleId } = this.props.match.params;
    const offset = this.state.resultsPerPage * (this.state.activePage - 1);

    if (initialLoad) {
      const { embed, embedInit } = parseQueryString(this.props.location.search);

      let canClose = query !== undefined;
      if (embedInit !== undefined) {
        canClose = false;
      }

      this.setState({
        canClose,
        embed
      } as any);
    }

    if (query) {
      this.setState({
        isLoading: true
      });
      console.log("query:", query);
      search({ bibleId, query, offset }).then(data => {
        this.setState({
          results: data.data,
          isLoading: false,
          fumsToken: data.meta.fumsToken || ""
        } as any);
      });
    }
  }

  fetchNextResults = () => {
    const { q } = parseQueryString(this.props.location.search);
    const query = "" + q;
    const { bibleId } = this.props.match.params;
    const { results } = this.state;
    const offset = results.verses.length;

    this.setState({
      isLoading: true
    });
    if (query) {
      this.setState({
        isLoading: true
      });
      search({ bibleId, query, offset }).then(data => {
        const newResults = [...results.verses, ...data.data.verses];
        this.setState({
          results: {
            ...this.state.results,
            verses: newResults
          },
          fumsToken: data.meta.fumsToken || "",
          isLoading: false
        } as any);
      });
    }
  };

  handlePageChange = (pageNumber: number) => {
    const { results } = this.state;
    const totalPages = Math.ceil(results.total / results.limit);
    if (pageNumber <= totalPages && pageNumber > 0) {
      this.setState({ activePage: pageNumber });
    }
  };

  handleLoadMore = () => {
    this.fetchNextResults();
  };

  handleClose = () => {
    const { history } = this.props;
    history.goBack();
  };

  renderPagination = (results: SearchResultData) => {
    const numOfPages = results ? Math.ceil(results.total / results.limit) : 0;
    const { isLoading } = this.state;

    if (isMobileDevice()) {
      return results.verses.length < results.total ? (
        <LoadMore>
          {isLoading ? (
            <Loader size={30} />
          ) : (
            <LoadMoreIcon size={30} onClick={this.handleLoadMore} />
          )}
        </LoadMore>
      ) : null;
    }
    return numOfPages < 2 ? null : (
      <Paginator
        onPageChange={this.handlePageChange}
        numOfPages={numOfPages}
        currentPage={this.state.activePage}
      />
    );
  };

  render() {
    const { q: query } = parseQueryString(this.props.location.search);
    const { bibleId } = this.props.match.params;
    const { isOnline, darkMode } = this.props;
    const { isLoading, canClose } = this.state;
    const results = this.state.results || ({} as SearchResultData);
    let { verses = [], passages = [] } = results;

    // fix for a bug in the api when valid, but non-existent passage id's are provided
    if (passages.length > 0) {
      passages = passages.filter(passage => passage);
    }

    if (passages.length === 1) {
      const passage = passages[0];
      this.props.history.push(
        chapterPath({
          bibleId,
          chapterId: passage.chapterIds[0],
          passageId: passage.orgId,
          location: this.props.location
        })
      );
      return null;
    }

    const areResults =
      query && !isLoading && passages && passages.length > 0 && passages[0];

    return (
      <Viewer>
        <AppBar showMenuButton>
          <AppBarSpacer />

          <PickerContainer>
            <PickerGroup>
              <SearchButtonItem>
                <SearchIcon size={22} />
              </SearchButtonItem>
              <SearchInput />
              {canClose && (
                <StyledButtonItem onClick={this.handleClose} key="close-search">
                  <CloseIcon size={16} />
                </StyledButtonItem>
              )}
            </PickerGroup>
          </PickerContainer>
        </AppBar>

        <div>
          {verses.map((verse, index) => (
            <SearchResult key={`${verse.id}-${index}`} verse>
              <Link
                to={chapterPath({
                  bibleId,
                  chapterId: verse.chapterId,
                  passageId: verse.orgId,
                  location: this.props.location
                })}
              >
                <h3>{verse.reference}</h3>
                <p>{verse.text}</p>
              </Link>
            </SearchResult>
          ))}

          {passages.map((passage, index) => (
            <SearchResult key={`${passage}-${index}`}>
              {passage.content.length > 520 && <BottomGradient />}
              <Passage bibleId={bibleId} passage={passage} link ellipsize />
            </SearchResult>
          ))}

          {results.total > 0 && this.renderPagination(results)}

          {isLoading && <Loader style={{ margin: "150px auto" }} />}

          {!isOnline && !areResults && (
            <StyledEmptyContent>
              <CloudOffIcon
                size={128}
                color={darkMode ? "#666" : "#e8e8e8"}
                style={{ margin: "150px auto" }}
              />
            </StyledEmptyContent>
          )}

          {!areResults && isOnline && (
            <StyledEmptyContent>
              <NoResultsIcon
                size="128"
                style={{
                  margin: "150px 0",
                  color: darkMode ? "#666" : "#e8e8e8"
                }}
              />
            </StyledEmptyContent>
          )}
        </div>
      </Viewer>
    );
  }
}

export default (
  props: Omit<Omit<SearchResultsProps, "isOnline">, "darkMode">
) => {
  usePageTracker();
  const isOnline = useNetworkStatus();
  const { darkMode } = useUserSettings();

  return <SearchResults darkMode={darkMode} isOnline={isOnline} {...props} />;
};
